import React from 'react';
import PropTypes from 'prop-types';
import CalciteModal from '@/components/shared/CalciteModal';
import AdaptiveAspectImage from '@/components/SearchResults/components/global/AdaptiveAspectImage';
import dayjs from 'dayjs';
import AddToCartButton from '@/features/product/components/AddToCartButton';
import {useFormatPrice} from '@/hooks/useFormatPrice';
import {CART_SOURCE_TYPES} from '@/services/api/api-constants';
import {mapLineItem} from '@/services/api/utils/cart-line-item-mapper';
import {useSelectedZone} from '@/services/geoblocking/hooks/useSelectedZone';
import {useSelector} from 'react-redux';
import {selectCartId} from '@/features/cart/slices/cart-slice';
import parse from 'html-react-parser';
import ContactButton from '@/features/product/components/ContactButton';

const DEFAULT_LABELS = {};
const EbookDetailsModal = ({open, onClose, cardinfo, labels = DEFAULT_LABELS}) => {
  const zone = useSelectedZone();
  const cartId = useSelector(selectCartId);
  const {title, authors, image, shortDescription, longDescription, publishDate, eisbn, pageCount, price, sku}
    = cardinfo || {};
  const ebook = {
    title: Array.isArray(title) ? title.join(', ') : title,
    author: Array.isArray(authors) ? authors.join(', ') : authors,
    image,
    shortDescription: Array.isArray(shortDescription) ? shortDescription.join(', ') : shortDescription,
    longDescription: Array.isArray(longDescription) ? longDescription.join(', ') : longDescription,
    eisbn,
    pageCount,
    price,
    sku,
    publishDate: publishDate ? dayjs(publishDate).format('MMM D, YYYY') : null,
  };
  const formattedPrice = useFormatPrice(ebook.price);
  const isPurchasable = Boolean(ebook.sku) && Boolean(ebook.price);
  const items = isPurchasable
    ? [mapLineItem(ebook, {sourceType: CART_SOURCE_TYPES.EBOOK_SEARCH_RESULT_ITEM, quantity: 1})]
    : null;

  return (
    <CalciteModal
      className="ebook-details-modal"
      open={open}
      onClose={onClose}
      aria-labelledby="ebook-details-title"
      width="m"
    >
      <div slot="header" className="ebook-details-modal__header">
        <div className="ebook-details-modal__header-image">
          {ebook.image && (
            <AdaptiveAspectImage
              src={ebook.image}
              alt={ebook.title ? `${ebook.title} - ${labels.ebookCoverAlt}` : labels.ebookCoverAlt}
            />
          )}
        </div>
        <div className="ebook-details-modal__header-text">
          <h3 id="ebook-details-title" className="ebook-details-modal__header-title">
            {ebook.title}
          </h3>
          <p className="ebook-details-modal__header-author">
            {labels.ebookByLabel} {ebook.author}
          </p>
        </div>
      </div>
      <div slot="content" className="ebook-details-modal__content" tabIndex="0">
        <div className="ebook-details-modal__specs-container">
          <table className="ebook-details-modal__specs" role="table">
            <caption className="assistText">{labels.ebookDetailsProductDetailsLabel}</caption>
            <thead>
              <tr>
                <th className="ebook-details-modal__specs-header" scope="col">
                  {labels.ebookDetailsSalesPriceLabel}
                </th>
                <th className="ebook-details-modal__specs-header" scope="col">
                  {labels.ebookDetailsPublishDateLabel}
                </th>
                <th className="ebook-details-modal__specs-header" scope="col">
                  {labels.ebookDetailsPageCountLabel}
                </th>
                <th className="ebook-details-modal__specs-header" scope="col">
                  {labels.ebookDetailsEISBNLabel}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="ebook-details-modal__specs-data">
                  {isPurchasable ? formattedPrice : labels.ebookNoPriceLabel}
                </td>
                <td className="ebook-details-modal__specs-data">{ebook.publishDate}</td>
                <td className="ebook-details-modal__specs-data">{ebook.pageCount}</td>
                <td className="ebook-details-modal__specs-data">{ebook.eisbn}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="ebook-details-modal__description-container">
          <h4 className="ebook-details-modal__description-title">{labels.ebookDetailsBookDetailsTitle}</h4>
          <p className="ebook-details-modal__description">
            {(ebook.longDescription && parse(ebook.longDescription)) || ebook.shortDescription}
          </p>
        </div>
      </div>
      {isPurchasable
        ? (
            <AddToCartButton items={items} zone={zone} cartId={cartId} slot="primary" onSuccess={onClose} onError={onClose}>
              {labels.ebookAddToCartLabel}
            </AddToCartButton>
          )
        : (
            <ContactButton slot="primary" url={labels.ebookNoPriceLink} onClick={onClose}>{labels.ebookNoPriceLabel}</ContactButton>
          )}
    </CalciteModal>
  );
};

EbookDetailsModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  cardinfo: PropTypes.shape({
    title: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.string), PropTypes.string]),
    authors: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.string), PropTypes.string]),
    image: PropTypes.string,
    shortDescription: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.string), PropTypes.string]),
    longDescription: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.string), PropTypes.string]),
    publishDate: PropTypes.string,
    eisbn: PropTypes.string,
    pageCount: PropTypes.string,
    price: PropTypes.number,
  }),
  labels: PropTypes.shape({
    ebookAddToCartLabel: PropTypes.string,
    ebookByLabel: PropTypes.string,
    ebookCoverAlt: PropTypes.string,
    ebookNoPriceLabel: PropTypes.string,
    ebookNoPriceLink: PropTypes.string,
    ebookDetailsBookDetailsTitle: PropTypes.string,
    ebookDetailsProductDetailsLabel: PropTypes.string,
    ebookDetailsEISBNLabel: PropTypes.string,
    ebookDetailsPageCountLabel: PropTypes.string,
    ebookDetailsPublishDateLabel: PropTypes.string,
    ebookDetailsSalesPriceLabel: PropTypes.string,
  }),
};

export default EbookDetailsModal;
